import { useCallback, useState } from 'react';
import { NodeRowHeaderProps } from './components/toc-node-row/components/NodeRowHeader';
import { ScorableItemRowHeaderProps } from './components/scorable-item-row/components/ScorableItemRowHeader';
import MultiMemberScorableItemRow from './components/scorable-item-row/MultiMemberScorableItemRow';
import SingleMemberScorableItemRow from './components/scorable-item-row/SingleMemberScorableItemRow';
import MultiMemberTocNodeRow from './components/toc-node-row/MultiMemberTocNodeRow';
import SingleMemberTocNodeRow from './components/toc-node-row/SingleMemberTocNodeRow';
import useTableData from './hooks/useTableData';
import { useFilters } from '../../context/filter-context/FilterContext';

interface TableBodyProps
  extends Pick<NodeRowHeaderProps, 'node'>,
    Pick<ReturnType<typeof useTableData>, 'getResultForTocNode' | 'getResultForScorableMaterial'> {
  showDetailView: boolean;
}

export default function TableBody({
  node,
  getResultForTocNode,
  getResultForScorableMaterial,
  showDetailView,
}: TableBodyProps) {
  const { selectedMembers, selectedGroups } = useFilters();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => setIsCollapsed(prev => !prev), []);

  if (!selectedGroups.length) return null;

  const membersToShow = selectedMembers[selectedGroups[0].id];

  if (!membersToShow.length) return null;

  return (
    <>
      <tbody data-testid={`toc-node-${node.id}`}>
        {!showDetailView ? (
          <MultiMemberTocNodeRow
            members={membersToShow}
            node={node}
            isCollapsed={isCollapsed}
            toggleCollapsed={toggleCollapsed}
            getResult={getResultForTocNode}
          />
        ) : (
          <SingleMemberTocNodeRow
            member={membersToShow[0]}
            node={node}
            isCollapsed={isCollapsed}
            toggleCollapsed={toggleCollapsed}
            getResult={getResultForTocNode}
          />
        )}

        {!isCollapsed &&
          node.scorableItems?.map(scorableItem =>
            !showDetailView ? (
              <MultiMemberScorableItemRow
                key={`${scorableItem.materialId}${scorableItem.scorableItemType}`}
                scorableItem={scorableItem}
                level={(node.level + 1) as ScorableItemRowHeaderProps['level']}
                members={membersToShow}
                getResult={getResultForScorableMaterial}
              />
            ) : (
              <SingleMemberScorableItemRow
                scorableItem={scorableItem}
                level={(node.level + 1) as ScorableItemRowHeaderProps['level']}
                group={selectedGroups[0]}
                member={membersToShow[0]}
                key={`result-${membersToShow[0].id}-${scorableItem.materialId}-${scorableItem.scorableItemType}`}
                getResult={getResultForScorableMaterial}
              />
            ),
          )}
      </tbody>
      {!isCollapsed &&
        node.nodes?.map(childNode => (
          <TableBody
            key={`node-${childNode.id}`}
            node={childNode}
            getResultForTocNode={getResultForTocNode}
            getResultForScorableMaterial={getResultForScorableMaterial}
            showDetailView={showDetailView}
          />
        ))}
    </>
  );
}
